import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewRef
} from '@angular/core';
import * as moment from 'moment';
import { Trip } from '../../shared/trip.model';
import { Stats } from '../../shared/stat.model';
import { QuickFilter } from '../../shared/quick-filter';
import { TripDelayType } from '../../shared/trip-delay-type';
import { TripStatus } from '../../shared/trip-status';
import { EtaEmitter } from '../../shared/eta-emitter.enum';
import { TripSortableColumn } from '../../shared/trip-sortable-column';
import { SortDirection } from '../../../shared/sort-direction';
import { DateTimezoneService } from '../../shared/date-timezone.service';
import { DateService } from '../../../shared/date.service';
import { TimelineInfo } from '../../shared/timeline-info';
import { InfoType } from '../../shared/info-type';

@Component({
  selector: 'app-load-tracking-overview',
  templateUrl: './load-tracking-overview.component.html',
  styleUrls: ['./load-tracking-overview.component.scss']
})
export class LoadTrackingOverviewComponent implements OnInit, OnChanges, DoCheck, OnDestroy {

  @Input()
  public bucketTrips: Trip[];
  @Input()
  public selectedTrip: Trip;
  @Input()
  public stats: Stats;
  @Input()
  public showBucket: boolean;
  @Output()
  public showBucketChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  public selectedTripChange: EventEmitter<Trip> = new EventEmitter<Trip>();
  @Output()
  public unselectTrip: EventEmitter<Trip> = new EventEmitter<Trip>();
  @Output()
  public switcher: EventEmitter<any> = new EventEmitter();
  @Input()
  public quickFilter: QuickFilter;
  @Output()
  public quickFilterChange: EventEmitter<QuickFilter> = new EventEmitter<QuickFilter>();
  public QuickFilter = QuickFilter;
  @Input()
  public date: string;
  @Output()
  public dateChange: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  public scrolled: EventEmitter<any> = new EventEmitter();
  @Input()
  public sort: TripSortableColumn;
  @Input()
  public sortDirection: SortDirection;
  @Input()
  public statusSortDirection: TripStatus;
  @Output()
  public sortChange: EventEmitter<TripSortableColumn>        = new EventEmitter();
  @Output()
  public statusSortDirectionChange: EventEmitter<TripStatus> = new EventEmitter();
  @Output()
  public sortDirectionChange: EventEmitter<SortDirection>    = new EventEmitter();

  public EtaEmitter = EtaEmitter;
  private differ: IterableDiffer<Trip>;
  public displayedTrips: Trip[] = [];
  public tripInDay: boolean[] = [];
  now: moment.Moment;
  min: moment.Moment;
  private max: moment.Moment;
  public dateDisplay: moment.Moment;
  public nowPosition: number;
  public nowOnDate: boolean;
  public tripDelay: { duration: string, key: string}[] = [];
  public tripStatus: TripDelayType[] = [];
  public TripStatus = TripStatus;
  public tripStartPosition: number[] = [];
  public tripWidth: number[] = [];
  public tripRemaining: number[] = [];
  public tripStartBeforeDate: boolean[] = [];
  public tripEndAfterDate: boolean[] = [];
  public tripEtaPosition: number[] = [];
  public tripDeadlineDisplay: string[] = [];
  public tripDeadlinePosition: string[] = [];
  public tripSecondaryEtaPosition: number[] = [];
  public tripEtaDisplay: string[] = [];
  public tripInfo: TimelineInfo[] = [];
  public tripRange: string[] = [];
  public scales: string[] = [];
  public scalePosition: number[] = [];
  public planningStart: number[] = [];
  public planningEnd: number[] = [];
  public planningInDay: boolean[] = [];
  public planningRange: string[] = [];
  private refreshInterval: any;
  public isTableScrollable = false;

  @ViewChild('table', { read: ElementRef, static : false })
  public table: ElementRef;

  protected readonly top = top;

  constructor(
    private cdr: ChangeDetectorRef,
    private differs: IterableDiffers,
    public timezoneService: DateTimezoneService,
    private dateService: DateService,
    private dateTimezoneService: DateTimezoneService,
  ) {
    this.dateService.localeChanged.subscribe(() => {
      if (this.bucketTrips) {
        this.ngOnInit();
      }
    });
    this.timezoneService.timezoneChange.subscribe(() => {
      if (this.bucketTrips !== undefined) {
        this.ngOnInit()
        if (!this.nowOnDate) {
          this.timezoneService.utcToCurrentTimezone(moment()).isAfter(this.max) ? this.nextDay() : this.previousDay();
        }
      }
    })
  }

  public ngOnInit(): void {
    this.setDay();
    this.setNow();
    this.scales = Array(9).fill(0).map((x, i) =>
      this.dateService.formatWithLocale(this.min.clone().add(i * 3, 'h'), 'LT'));
    this.scalePosition = Array(9).fill(0).map((x, i) =>
      this.min.clone().add(i * 3, 'h').diff(this.min) * 100 / this.max.diff(this.min));
    this.differ = this.differs.find(this.bucketTrips).create(null);
    this.refreshInterval = setInterval(() => this.calculateDisplay(), 60000);
  }

  public ngDoCheck(): void {
    const bucketChanges = this.differ?.diff(this.bucketTrips);
    if (bucketChanges) {
      this.ngOnChanges({bucketTrips: {previousValue: null, currentValue: null, firstChange: null, isFirstChange: null}})
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.bucketTrips) {
      if (!this.min || !this.max) {
        this.ngOnInit();
      }
      this.displayedTrips = [...this.bucketTrips];
      this.calculateDisplay();
      setTimeout(() => this.isTableScrollable = this.table.nativeElement.scrollHeight > this.table.nativeElement.offsetHeight, 0);
    }
  }

  public ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
  }

  public determineDelayStatuses(): void {
    if (this.bucketTrips) {
      this.bucketTrips.map(trip => {
        const tripState = trip.getTripState(this.dateService);
        this.tripDelay[trip.tripId] = {duration: tripState.msgDate, key: tripState.msgKey};
        this.tripStatus[trip.tripId] = tripState.status;
      });
    }
  }

  private calculateDisplay(): void {
    this.setNow();
    this.determineDelayStatuses();
    for (const trip of this.displayedTrips) {
      const tripStart = this.timezoneService.utcToCurrentTimezone(trip.getTripStart());
      const tripEnd = this.timezoneService.utcToCurrentTimezone(trip.getTripEnd(true));
      if (
          trip.status !== TripStatus.PLANNED &&
          (
            (tripStart.isBetween(this.min, this.max) || tripEnd.isBetween(this.min, this.max)) ||
            (this.min.isBetween(tripStart, tripEnd) && this.max.isBetween(tripStart, tripEnd))
          )
      ) {
        this.tripInDay[trip.tripId] = true;
        let startPos;
        let width;
        let remaining;
        if (this.now.isBefore(tripEnd)) {
          startPos = this.clamp(0, tripStart.diff(this.min) * 100 / this.max.diff(this.min), 100);
          width = this.clamp(0, this.now.diff(this.min) * 100 / this.max.diff(this.min), 100) - startPos;
          remaining = this.clamp(0, tripEnd.diff(this.min) * 100 / this.max.diff(this.min), 100);
        } else {
          startPos = Math.max(tripStart.diff(this.min) * 100 / this.max.diff(this.min), 0);
          width = Math.min(Math.abs(tripEnd.diff(this.min)) * 100 /
            this.max.diff(this.min), 100) - startPos;
          remaining = 0;
        }
        this.tripStartPosition[trip.tripId] = startPos;
        this.tripWidth[trip.tripId] = width;
        this.tripRemaining[trip.tripId] = remaining;

        if (trip.latestEta()) {
          if (trip.driverEta) {
            const driverEta = this.timezoneService.utcToCurrentTimezone(trip.driverEta);
            this.tripEtaPosition[trip.tripId] = driverEta.isBetween(this.min, this.max, undefined, '[]') ?
              driverEta.diff(this.min) * 100 / this.max.diff(this.min) :
              -1;
          } else {
            this.tripEtaPosition[trip.tripId] = -1;
          }
        }
        this.tripRange[trip.tripId] = this.dateService.formatWithLocale(tripStart, 'D MMM LT') + ' - ' + this.dateService.formatWithLocale(tripEnd, 'D MMM LT');
      } else {
        this.tripStartPosition[trip.tripId] = -1;
        this.tripWidth[trip.tripId] = -1;
        this.tripEtaPosition[trip.tripId] = -1;
        this.tripInDay[trip.tripId] = false;
      }
      // always display automatic eta if present
      this.tripSecondaryEtaPosition[trip.tripId] = trip.latestEta() && trip.automaticEta ? this.timezoneService.utcToCurrentTimezone(trip.automaticEta).diff(this.min) * 100 / this.max.diff(this.min) : -1;

      const loadPlanningStart = trip.loadPlanningStart ? this.timezoneService.utcToCurrentTimezone(trip.loadPlanningStart) : null;
      const loadPlanningEnd = trip.loadPlanningEnd ? this.timezoneService.utcToCurrentTimezone(trip.loadPlanningEnd) : null;
      if (
        loadPlanningStart && loadPlanningEnd &&
        (
          (loadPlanningStart.isBetween(this.min, this.max) || loadPlanningEnd.isBetween(this.min, this.max)) ||
          (this.min.isBetween(loadPlanningStart, loadPlanningEnd) && this.max.isBetween(loadPlanningStart, loadPlanningEnd))
        )
      ) {
        this.planningInDay[trip.tripId] = true;
        this.planningStart[trip.tripId] = Math.max(Math.abs(loadPlanningStart.diff(this.min)) * 100 / this.max.diff(this.min), 0);
        this.planningEnd[trip.tripId] = 100 - Math.min(Math.abs(loadPlanningEnd.diff(this.min)) * 100 / this.max.diff(this.min), 100);
        this.planningRange[trip.tripId] = this.dateService.formatWithLocale(loadPlanningStart, 'D MMM LT') + ' - ' + this.dateService.formatWithLocale(loadPlanningEnd, 'D MMM LT');
      } else {
        this.planningInDay[trip.tripId] = false;
        this.planningStart[trip.tripId] = null;
        this.planningEnd[trip.tripId] = null;
        this.planningRange[trip.tripId] = null;
      }
      this.tripDeadlineDisplay[trip.tripId] = loadPlanningEnd ? this.dateService.formatWithLocale(loadPlanningEnd, 'LT') : 'Unknown';
      this.tripDeadlinePosition[trip.tripId] = loadPlanningEnd && loadPlanningEnd.isBetween(this.min, this.max) ?
        this.clamp(0, loadPlanningEnd.diff(this.min) * 100 / this.max.diff(this.min), 100) : -1;
      this.tripEtaDisplay[trip.tripId] = trip.latestEta() ? this.dateService.formatWithLocale(trip.latestEta(), 'LT') : '-';
      this.tripStartBeforeDate[trip.tripId] = tripStart ? tripStart.isBefore(this.min) : null;
      this.tripEndAfterDate[trip.tripId] = tripEnd ? tripEnd.isAfter(this.max) : null;
      this.tripInfo[trip.tripId] = this.createTripInfo(trip);
    }
    // PREVENTS THE USAGE OF detectChanges IF THE VIEW IS ALREADY DESTROYED
    if (this.cdr && !(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }
  }

  public setNow(): void {
    this.now = this.timezoneService.utcToCurrentTimezone(moment());
    this.nowPosition = this.now.diff(this.min) * 100 / this.max.diff(this.min);
  }

  public switchQuickFilter(filter: QuickFilter): void {
    if (this.quickFilter !== filter) {
      this.quickFilter = filter;
    } else {
      this.quickFilter = QuickFilter.RESET;
    }
    this.quickFilterChange.emit(this.quickFilter);
  }

  public selectTripAndSeeOnMap(trip: Trip): void {
    this.selectedTrip = trip;
    this.selectedTripChange.emit(trip);
    this.switchMode();
  }

  public switchTrip(trip: Trip): void {
    if (this.selectedTrip === trip) {
      this.unselectTrip.emit();
    } else {
      this.selectTripTimezone(trip.timezone);
      this.selectedTripChange.emit(trip);
    }
  }

  public nextDay(): void {
    this.min = this.min.add(1, 'day').clone();
    this.max.add(1, 'day');
    this.date = this.dateService.formatWithLocale(this.min, 'YYYY-MM-DD');
    this.dateChange.emit(this.date);
    this.nowOnDate = this.timezoneService.utcToCurrentTimezone(moment()).isBetween(this.min, this.max, undefined, '[]');
    this.calculateDisplay();
  }

  public previousDay(): void {
    this.min = this.min.add(-1, 'day').clone();
    this.max.add(-1, 'day');
    this.date = this.dateService.formatWithLocale(this.min, 'YYYY-MM-DD');
    this.dateChange.emit(this.date);
    this.nowOnDate = this.timezoneService.utcToCurrentTimezone(moment()).isBetween(this.min, this.max, undefined, '[]');
    this.calculateDisplay();
  }

  private createTripInfo(trip: Trip): TimelineInfo {
    if (!trip.loadPlanningEnd && !trip.driverEta && !trip.automaticEta) {
      return  null;
    }
    return  {
      time1: trip.loadPlanningEnd,
      time2: trip.latestEta(),
      etaSource: trip.latestEtaOrigin(),
      preferredTimeZone: trip.timezone,
      type: InfoType.OVERVIEW,
      active: false,
      pos: null, duration: null, endDate: null, distance: null, distanceTravelled: null, distanceRemaining: null,
      distanceUnit: null, handoverVehicle1: null, handoverVehicle2: null, provider: null,
    }
  }

  public setDay(): void {
    this.min = this.timezoneService.utcToCurrentTimezone(moment(this.date)).startOf('day');
    this.max = this.timezoneService.utcToCurrentTimezone(moment(this.date)).endOf('day');
    this.dateDisplay = this.min.clone();
    this.nowOnDate = this.timezoneService.utcToCurrentTimezone(moment()).isBetween(this.min, this.max, undefined, '[]');
    this.calculateDisplay();
  }

  public setDayToTripEnd(trip: Trip, e): void {
    e.stopPropagation();
    this.date = this.dateService.formatWithLocale(this.timezoneService.utcToCurrentTimezone(trip.getTripEnd(true)),  'YYYY-MM-DD');
    this.dateChange.emit(this.date);
    this.setDay();
  }

  public leftArrowClicked(trip: Trip, e): void {
    e.stopPropagation();
    this.date = this.dateService.formatWithLocale(this.timezoneService.utcToCurrentTimezone(trip.getTripStart()),  'YYYY-MM-DD');
    this.setDay();
  }

  public rightArrowClicked(trip: Trip, e): void {
    e.stopPropagation();
    const tripStart = this.timezoneService.utcToCurrentTimezone(trip.getTripStart());
    if (tripStart.isAfter(this.max)) {
      this.date = this.dateService.formatWithLocale(tripStart, 'YYYY-MM-DD');
    } else {
      this.date = this.dateService.formatWithLocale(this.timezoneService.utcToCurrentTimezone(trip.getTripEnd(true)), 'YYYY-MM-DD');
    }
    this.setDay();
  }

  public changeSortDirection(): void {
    if (this.sort === TripSortableColumn.STATUS) {
      switch (this.statusSortDirection) {
        case TripStatus.IN_PROGRESS:
          this.statusSortDirection = TripStatus.COMPLETED;
          break;
        case TripStatus.COMPLETED:
          this.statusSortDirection = TripStatus.EXPIRED;
          break;
        case TripStatus.EXPIRED:
          this.statusSortDirection = TripStatus.CANCELLED;
          break;
        case TripStatus.CANCELLED:
          this.statusSortDirection = TripStatus.PLANNED;
          break;
        case TripStatus.PLANNED:
          this.statusSortDirection = TripStatus.IN_PROGRESS;
          break;
      }
      this.statusSortDirectionChange.emit(this.statusSortDirection);
    } else {
      this.sortDirection = this.sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
      this.sortDirectionChange.emit(this.sortDirection);
    }
  }

  public switchMode(): void {
    this.switcher.emit();
  }

  public clamp(min: number, val: number, max: number): number {
    return Math.max(Math.min(val, max), min);
  }

  public selectTripTimezone(timezone: string) {
    this.dateTimezoneService.timezoneSourceChange(false, timezone);
  }
}
